import {
  Dialog,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { ReactNode } from 'react'

export interface FullScreenModalProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  primaryAction?: {
    label: string
    onClick: () => void
    disabled?: boolean
    loading?: boolean
  }
  secondaryAction?: {
    label: string
    onClick: () => void
    disabled?: boolean
  }
}

export function FullScreenModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  primaryAction,
  secondaryAction
}: FullScreenModalProps) {
  // Always use fullScreen for our component
  const fullScreen = true

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullScreen={fullScreen}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          margin: 0,
          width: '100vw',
          height: '100vh',
          maxWidth: 'none',
          maxHeight: 'none',
          borderRadius: 0
        }
      }}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: '100vw',
          height: '100vh'
        }
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          minHeight: 72,
          flexShrink: 0
        }}
      >
        {/* Left section */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <IconButton
            onClick={onClose}
            size="medium"
            sx={{ 
              mr: 2,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'text.primary'
              }
            }}
          >
            <Close />
          </IconButton>
          
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography 
              variant="h5" 
              component="h1"
              noWrap
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '1.5rem',
                lineHeight: 1.2
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                noWrap
                sx={{ 
                  mt: 0.5,
                  fontSize: '0.875rem'
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        
        {/* Right section - Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 3 }}>
          {secondaryAction && (
            <Button
              variant="outlined"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                px: 2.5,
                py: 1,
                borderRadius: 1.5
              }}
            >
              {secondaryAction.label}
            </Button>
          )}
          
          {primaryAction && (
            <Button
              variant="contained"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled || primaryAction.loading}
              startIcon={primaryAction.loading ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 1.5,
                minWidth: 120
              }}
            >
              {primaryAction.loading ? 'Carregando...' : primaryAction.label}
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Content */}
      <Box 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: 'background.default'
        }}
      >
        {children}
      </Box>
    </Dialog>
  )
}

export default FullScreenModal